import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LANDING } from '@/constants/testIds';
import { ArrowRight, Menu, X } from 'lucide-react';
import LogoMark from './LogoMark';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isLanding = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <nav
      data-testid={LANDING.nav}
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-void/90 backdrop-blur-xl border-b border-signal-gold/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto flex justify-between items-center py-5 px-6 md:px-12">
        <Link
          data-testid={LANDING.navLogo}
          to="/"
          className="flex items-center gap-2.5 group"
        >
          <LogoMark size={26} className="transition-transform duration-300 group-hover:scale-110" />
          <span className="font-heading text-2xl font-semibold text-bone tracking-tight group-hover:text-signal-gold transition-colors duration-300">
            Vis<span className="text-signal-gold">IQ</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {isLanding ? (
            <>
              <a
                data-testid={LANDING.navComponents}
                href="#components"
                className="font-body text-sm text-slate hover:text-bone transition-colors duration-300 relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-px after:bg-signal-gold after:transition-all after:duration-300 hover:after:w-full"
              >
                Components
              </a>
              <a
                data-testid={LANDING.navTiers}
                href="#tiers"
                className="font-body text-sm text-slate hover:text-bone transition-colors duration-300 relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-px after:bg-signal-gold after:transition-all after:duration-300 hover:after:w-full"
              >
                Tiers
              </a>
            </>
          ) : (
            <Link
              to="/"
              className="font-body text-sm text-slate hover:text-bone transition-colors duration-300 relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-px after:bg-signal-gold after:transition-all after:duration-300 hover:after:w-full"
            >
              Home
            </Link>
          )}
          <Link
            data-testid="landing-nav-partnerships"
            to="/partnerships"
            className={`font-body text-sm transition-colors duration-300 relative after:absolute after:bottom-[-4px] after:left-0 after:h-px after:bg-signal-gold after:transition-all after:duration-300 hover:after:w-full ${
              location.pathname === '/partnerships' ? 'text-bone after:w-full' : 'text-slate hover:text-bone after:w-0'
            }`}
          >
            Partnerships
          </Link>
          <a
            data-testid={LANDING.navCta}
            href="https://app.visiq.es"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-bone text-void font-body font-medium text-sm px-6 py-2.5 rounded-none hover:bg-signal-gold transition-colors duration-300"
          >
            Get started
            <ArrowRight size={14} strokeWidth={1.5} />
          </a>
        </div>

        {/* Mobile menu button */}
        <button
          data-testid="landing-nav-mobile-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden text-bone p-1"
        >
          {mobileOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-void/95 backdrop-blur-xl border-t border-white/5 px-6 py-6 flex flex-col gap-5 animate-fade-in">
          {isLanding && (
            <>
              <a href="#components" className="font-body text-sm text-slate" onClick={() => setMobileOpen(false)}>Components</a>
              <a href="#tiers" className="font-body text-sm text-slate" onClick={() => setMobileOpen(false)}>Tiers</a>
            </>
          )}
          {!isLanding && (
            <Link to="/" className="font-body text-sm text-slate">Home</Link>
          )}
          <Link to="/partnerships" className="font-body text-sm text-slate">Partnerships</Link>
          <a
            href="https://app.visiq.es"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-bone text-void font-body font-medium text-sm px-5 py-2.5 rounded-none w-fit"
            data-testid="landing-nav-mobile-cta"
          >
            Get started
            <ArrowRight size={14} strokeWidth={1.5} />
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
